import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { MapPin, Package, CheckCircle, WhatsappLogo, ShareNetwork } from "@phosphor-icons/react";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const EMPTY_FORM = { name: "", phone: "", material: "", quantity: "", site: "", item_id: null };

function formatQty(q) {
  const n = Number(q);
  return Number.isInteger(n) ? n.toLocaleString("en-IN") : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function OldMaterialPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Old Scaffolding Material for Sale | Scaffo India";
    api
      .get("/public/old-material")
      .then((res) => setItems(res.data.items || []))
      .catch(() => toast.error("Could not load available material"))
      .finally(() => setLoading(false));
  }, []);

  const sites = useMemo(() => [...new Set(items.map((i) => i.site))].sort(), [items]);
  const materials = useMemo(() => [...new Set(items.map((i) => i.material))].sort(), [items]);
  const visible = siteFilter === "all" ? items : items.filter((i) => i.site === siteFilter);

  const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value, ...(key !== "quantity" ? { item_id: null } : {}) }));

  const pickItem = (item) => {
    setForm((f) => ({ ...f, material: item.material, site: item.site, item_id: item.id }));
    setSubmitted(false);
    document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/old-material/enquiries", { ...form, quantity: Number(form.quantity) });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString?.() || "Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shareLink = async () => {
    const url = window.location.origin + "/old-material";
    try {
      if (navigator.share) await navigator.share({ title: "Old scaffolding material for sale", url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {
      /* user cancelled */
    }
  };

  const inputClass =
    "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20";

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      <div style={{ background: "#141414" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
            Old material · Ready stock
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight max-w-2xl">
            Used scaffolding material for sale
          </h1>
          <p className="mt-3 text-sm sm:text-base text-neutral-400 max-w-xl">
            See what's available at each site and send a buy enquiry. Our team will call you back with price and pickup details.
          </p>
          <button
            type="button"
            onClick={shareLink}
            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-neutral-700 px-4 py-2 text-sm font-semibold text-white hover:border-[#FF7A00]"
          >
            <ShareNetwork size={16} weight="bold" /> Share this page
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-8 lg:grid-cols-[1fr_380px]">
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="text-lg font-bold text-neutral-900">Available by site</h2>
            {sites.length > 1 && (
              <select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
                data-testid="old-material-site-filter"
              >
                <option value="all">All sites</option>
                {sites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-neutral-500">Loading…</p>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-600">
              No old material is listed right now. Send an enquiry with what you need and we'll let you know when it's available.
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 list-none p-0 m-0">
              {visible.map((item) => (
                <li key={item.id}>
                  <div
                    className="rounded-2xl h-full overflow-hidden bg-white flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ border: "1px solid #E8E3D9", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                    data-testid="old-material-card"
                  >
                    <div className="h-24 flex items-center justify-center" style={{ background: "#F7F4EF" }}>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(255,122,0,0.12)" }}>
                        <Package size={28} weight="bold" style={{ color: "#FF7A00" }} />
                      </div>
                    </div>
                    <div className="p-4 sm:p-5 flex flex-col flex-1">
                      <h3 className="font-black text-neutral-900 text-sm leading-snug">{item.material}</h3>
                      <div className="flex flex-wrap gap-1 mt-2 mb-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{ background: "rgba(255,122,0,0.1)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.2)" }}
                        >
                          {formatQty(item.quantity)} {item.unit || "pcs"} available
                        </span>
                        {item.condition && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                            style={{ background: "#F0EBE3", color: "#888", border: "1px solid #E8E3D9" }}
                          >
                            {item.condition}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-auto">
                        <span className="flex items-center gap-1.5 min-w-0 text-xs font-semibold" style={{ color: "#888" }}>
                          <MapPin size={13} weight="bold" className="shrink-0" style={{ color: "#C4A882" }} />
                          <span className="truncate">{item.site}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => pickItem(item)}
                          className="shrink-0 rounded-full px-4 py-1.5 text-xs font-bold text-white hover:opacity-90"
                          style={{ background: "#FF7A00" }}
                          data-testid={`old-material-enquire-${item.id}`}
                        >
                          Enquire
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <aside id="enquiry-form" className="lg:sticky lg:top-24 self-start rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="text-lg font-bold text-neutral-900">Buy enquiry</h2>
          {submitted ? (
            <div className="mt-4 text-center py-6">
              <CheckCircle size={40} weight="fill" className="mx-auto" style={{ color: "#16a34a" }} />
              <p className="mt-3 font-semibold text-neutral-900">Thanks! Enquiry received.</p>
              <p className="mt-1 text-sm text-neutral-600">Our team will call you shortly.</p>
              <button type="button" onClick={() => setSubmitted(false)} className="mt-4 text-sm font-semibold" style={{ color: "#FF7A00" }}>
                Send another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-4 space-y-3" data-testid="old-material-enquiry-form">
              <label className="block">
                <span className="text-xs font-semibold text-neutral-700">Name</span>
                <input required minLength={2} maxLength={80} value={form.name} onChange={onField("name")} className={inputClass} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-neutral-700">Phone</span>
                <input required type="tel" inputMode="tel" maxLength={15} value={form.phone} onChange={onField("phone")} className={inputClass} placeholder="10-digit mobile" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-neutral-700">Material</span>
                <input required list="old-material-options" maxLength={120} value={form.material} onChange={onField("material")} className={inputClass} placeholder="e.g. Cuplock vertical 3m" />
                <datalist id="old-material-options">
                  {materials.map((m) => <option key={m} value={m} />)}
                </datalist>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-neutral-700">Quantity</span>
                  <input required type="number" min="1" step="any" value={form.quantity} onChange={onField("quantity")} className={inputClass} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-neutral-700">Site</span>
                  <input required list="old-material-sites" maxLength={120} value={form.site} onChange={onField("site")} className={inputClass} />
                  <datalist id="old-material-sites">
                    {sites.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </label>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg py-3 text-sm font-bold text-white disabled:opacity-60"
                style={{ background: "#FF7A00" }}
              >
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
              <p className="flex items-center gap-1.5 text-[11px] text-neutral-500">
                <WhatsappLogo size={14} /> We usually respond within one working day.
              </p>
            </form>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}
